import type { Model } from "./Model"
import type { Localized } from "../packages/localization/Localized"
import {
    Discriminate,
    GetTypeAlias,
    GetTypeProps,
    IsArrayType,
    IsUnionType,
    MatchesTypeTurbo,
    ObjectType,
    Property,
    Type,
} from "./Types/Type"
import { Assert } from "./Helpers"
import { Tags } from "./Types/Tags"
import type { File } from "./Types/File"
import { EmailTemplate } from "../packages/email/EmailTemplate"

export type CollectionType = ObjectType & { collection: string; typeArgs: [Type] }

/**
 * @shared
 */
export type CollectionDesc = {
    name: string
    /** Dictionary of the name in different languages. */
    nameTranslations: Localized<string>

    /** Dictionary of the description in different languages. */
    descriptionTranslations: Localized<string>

    /** Display name of a single item in this collection, e.g. "User" */
    itemName: Localized<string>

    type: CollectionType
    isSingleton: boolean
    primaryKey: string
    description?: string
    /** If specified, this overrides the document type that should be used when validating this
     * document in Reactor Studio. */
    studioType?: string
    studioGroup: string
    icon?: string
    /**
     * The name of the service constructor that this collection belongs to, if any.
     */
    service?: string
}
/**
 * @shared
 */
export type TriggerDesc = {
    fileName: string
    name: string
    description?: string
    arg: ObjectType
    tags?: Tags
}

/**
 * @shared
 */
export type ArgDesc = {
    name: string
    type: Type
    optional: boolean
    defaultValue?: any
    comments?: string
    tags?: Tags
    /** Whether this is a rest parameter, i.e. (...args: any[]) */
    dotDot?: boolean
}
/**
 * @shared
 */
export type CallDesc = {
    /** Name of the function being called */
    name: string

    /** The file in which the call target is declared. */
    declFileName?: string

    /** Description of the call site (extracted from comments) */
    description?: string

    /** Arguments to the call, if they were strings literals. */
    args: (string | undefined)[]

    /** What type of call this is, top-level function or class method. */
    type: "method" | "function"
}
/**
 * @shared
 */
export type FunctionDesc = CallGraphNode & {
    sourcePath: string
    isAsync: boolean
    /** Name of the argument that maps to the request body */
    body: string
    /** Name of the argument that maps to the request files */
    files: string
    /** Name of the field in the multipart form-data containing file */
    fileField: string
    /** Whether this endpoint performs transforms on the response body.
     */
    transform: boolean
    /** Whether this endpoint returns a localized response.
     *
     *  If `transform` is false, this is ignored, as that disables all
     *  transforms, including localization.
     */
    localize: boolean
    isInternal: boolean
    includeInApiSpec?: boolean
    modelName?: string
    /** The group that this function belongs to. Defaults to pretty-printed file name */
    group: string
    /** expected agent types, used to decide what api specs the function will appear in
     * (e.g. web, api)
     */
    agents: string[]
    tags?: Tags

    /**
     * The type that the transform plugins expect as input.
     *
     * This field should not be accessed directly, as it might not be
     * initialized yet. It is used internally in the transform system.
     *
     */
    transformPluginInputType?: Type

    /**
     * If this is an endpoint, this holds the transformed return type after it
     * has been initialized by GetTransformedReturnType.
     *
     * This field should not be accessed directly, but through
     * GetTransformedReturnType, for it to work correctly across reflection and
     * prebuilt reflection environments.
     */
    transformedReturnType?: Type

    /** The HTTP query path of this function, if it is an endpoint */
    path?: string
    /** The HTTP verb of this function, if it is an endpoint. */
    verb?: string | undefined

    /**
     * The title to display in an auto-generated UI for this function.
     */
    title?: Localized<string>
}
/**
 * @shared
 */
export type ThrowsDesc = {
    error: string
    errorType: Type
    argumentValues?: any[]
    description?: string
}
/**
 * @shared
 */
export type StatusDesc = {
    status: string
    code: number
    example?: string
    returnType: Type
    description?: string
}

/**
 * Shared base of FunctionDesc and Method, used to trace call graphs
 * to detect reachable error codes and status codes.
 * @shared
 */
export type CallGraphNode = {
    name: string

    /** Unique name in case of multiple instances of a service declaring this
     * function. */
    operationId?: string

    args: ArgDesc[]
    returns: Type
    description?: string

    /** The exceptions thrown by this function. Not available client-side. */
    throws?: ThrowsDesc[]
    status: StatusDesc[]
    /** Plain-named functions called by this function. Can be used to inherit
     * `throws` and other things from other `FunctionDesc`s.
     *
     *  Not available client-side.
     */
    calls?: CallDesc[]

    /** Whether this function is declared inside a `@service` constructor. */
    service?: string
}

/**
 * @shared
 */
export type Method = CallGraphNode

/**
 * @shared
 */
export type ClassDesc = {
    description?: string
    name: string
    props: Property[]
    methods: Method[]
    declarationOrder: string[]
    declFileName: string
}

/**
 * @shared
 */
export type TypeExtenderDesc = {
    sourcePath: string
    /** Names of the types being extended, in declaration order in the `sourcePath` file. */
    types: string[]
}
/**
 * @shared
 */
export type WidgetDesc = {
    sourcePath: string
    /** Names of the types for which widgets are declared, in declaration order, in the
     * `sourcePath` file. */
    types: string[]
}
/**
 * @shared
 */
export type DefaultDesc = {
    sourcePath: string
    /** Names of the types for which default functions are declared, in
     * declaration order, in the `sourcePath` file. */
    types: string[]
}
/**
 * @shared
 */
export type AutoFillDesc = {
    sourcePath: string
    /** Names of the types for which auto fill functions are declared, in
     * declaration order, in the `sourcePath` file. */
    types: string[]
}

/**
 * @shared
 */
export type TemplateDesc = {
    sourcePath: string

    /**
     * The name of the template. This is the name of the function.
     */
    name: string

    /** The arguments to the template function. These must be filled out before
     * the template can be invoked. */
    args: ArgDesc[]

    /**
     * Description of the template, extracted from comments.
     */
    description?: string

    tags?: Tags

    /** The type that this template produces */
    returns: Type
}

/**
 * @shared
 */
export type SectionDesc = {
    name: string
    type: ObjectType
    sourcePath: string
}

/**
 * @shared
 */
export type EmailSectionDesc = {
    name: string
    type: ObjectType
    sourcePath: string
}

/**
 * @shared
 */
export type ComponentDesc = {
    name: string
    type: ObjectType
    sourcePath: string
}

/**
 * @shared
 */
export type ReflectionInfo = {
    collections: CollectionDesc[]
    functions: FunctionDesc[]
    classes: ClassDesc[]
    triggers: TriggerDesc[]
    /** The server-side types */
    types: Type[]
    /** Types that are generated as DTOs for clients, that don't necessarily
     * match the server-side types. */
    clientTypes: {
        /** Types generated for the Reactor-internal APIs (e.g. Studio, Widget system) */
        internal: Type[]
        /** Types generated for the external API (i.e. model-specific) */
        external: Type[]
    }
    model: Model
    widgets: WidgetDesc[]
    defaults: DefaultDesc[]
    autoFills: AutoFillDesc[]
    templates: TemplateDesc[]
    sections: SectionDesc[]

    components: ComponentDesc[]
    typeExtenders: TypeExtenderDesc[]
    /** Documentation for the function groups */
    groupDocs: { [group: string]: string }
    /** The files referenced by the @defaultFile tag in the model */
    defaultFiles?: { [filename: string]: { file: File; mimeType: string; size: number } }

    emailSections: EmailSectionDesc[]
    emailTemplates: EmailTemplate[]
}

let resolveReflectionInfo: (ri: ReflectionInfo) => void
let info: ReflectionInfo
export function SetReflectionInfo(ri: ReflectionInfo) {
    info = ri
    info.types.forEach((type) => {
        // Don't add reference types to the dictionary
        if (typeof type === "object" && type.reference) return

        const alias = GetTypeAlias(type)
        if (alias) aliasToType.set(alias, type)
    })
    resolveReflectionInfo(ri)
}
export function GetReflectionInfo() {
    if (info === undefined) throw new Error("Cannot GetReflectionInfo prior to SetReflectionInfo")
    return info
}
export const ReflectionInfo = new Promise<ReflectionInfo>(
    (resolve) => (resolveReflectionInfo = resolve)
)
export function IsReflectionInfoReady() {
    return !!info
}

const aliasToType = new Map<string, Type>()

export type TypeLocatorWithPath = [typeName: string, ...path: (string | number)[]]
export type TypeLocator = string | TypeLocatorWithPath

/** Looks up a type by alias from the current reflection info.
 *
 * A path can also be provided to look up the sub-types of property or array
 * elements.
 *
 * @throws Error if the type is not found.
 * @throws Error if no reflection info is available.
 */
export function GetType(locator: TypeLocator): Type {
    if (!info) throw new Error("GetType(): No reflection info available")

    const alias = typeof locator === "string" ? locator : locator[0]
    const path = typeof locator === "string" ? [] : locator.slice(1)

    if (alias === "unknown") return "unknown"
    if (alias === "undefined") return "undefined"
    if (alias === "null") return "null"

    let type =
        aliasToType.get(alias) ??
        info.clientTypes.external.find((t) => GetTypeAlias(t) === alias) ??
        info.clientTypes.internal.find((t) => GetTypeAlias(t) === alias)
    if (!type) throw new Error("GetType(): Type not found: " + alias)

    if (!path.length) return type

    for (const part of path) {
        if (typeof part === "string") {
            const props = GetTypeProps(type)
            const prop = props.find((p) => p.name === part)
            if (!prop) throw new Error("GetType(): Property not found: " + part)
            type = prop.type
        } else if (typeof part === "number") {
            if (!IsArrayType(type)) throw new Error("GetType(): Property is not an array: " + part)
            type = type.array
        }
    }

    return type
}

const substituteTypesCache = new Map<Type, Type[]>()

/**
 * Returns all types that are marked with `@substitute` for the given type.
 *
 * Returns `undefined
 */
export function GetSubstituteTypes(type: Type) {
    if (!substituteTypesCache.has(type)) {
        substituteTypesCache.set(type, GetSubstituteTypesUncached(type))
    }
    return Assert(substituteTypesCache.get(type))
}

function GetSubstituteTypesUncached(type: Type) {
    const alias = GetTypeAlias(type)

    return alias
        ? GetReflectionInfo().types.filter(
              (t) => typeof t === "object" && t.tags?.substitute === alias
          )
        : []
}

const sectionsCache = new Map<string, ObjectType>()

/**
 * Determines the type of a value by considering `@substitute` tags .
 *
 * This first looks for types marked with `@substitute` for the given type. If
 * any are found, it will return the the first type found that matches the
 * provided value. If none are found, the original type will be returned.
 *
 * See docs for the `@substitute` tag for more information.
 */
export function SubstituteType(value: any, type: Type) {
    if (value === undefined) return type

    let types = GetSubstituteTypes(type)
    if (types.length === 1) {
        return types[0]
    }

    // Special case for section types
    if (GetTypeAlias(type) === "Section") {
        if ("type" in value) {
            if (!sectionsCache.size) {
                GetReflectionInfo().sections.forEach((s) => sectionsCache.set(s.name, s.type))
            }
            const section = sectionsCache.get(value.type)
            if (section) {
                return section
            }
        }

        types = [...types, ...GetReflectionInfo().sections.map((s) => s.type)]
    }

    // Special case for section types
    if (GetTypeAlias(type) === "EmailSection") {
        if ("type" in value) {
            if (!sectionsCache.size) {
                GetReflectionInfo().emailSections.forEach((s) => sectionsCache.set(s.name, s.type))
            }
            const section = sectionsCache.get(value.type)
            if (section) {
                return section
            }
        }

        types = [...types, ...GetReflectionInfo().sections.map((s) => s.type)]
    }

    return types.find((t) => MatchesTypeTurbo(value, t, false)) ?? type
}

/**
 * Determines the specific type of a value, considering `@substitute` tags before
 * discriminating.
 *
 * This first looks for types marked with `@substitute` for the given type. If
 * any are found, it will consider the the first type found that matches the
 * provided value. If none are found, the original type will be considered.
 *
 * If the considered type is a union, it will be discriminated against the value
 * before returned. If the type is not a union, it will be returned as-is.
 *
 * See docs for the `@substitute` tag for more information.
 */
export function SubstituteAndDiscriminate(value: any, type: Type) {
    type = SubstituteType(value, type)
    return IsUnionType(type) ? Discriminate(value, type) : type
}

/**
 * Returns the primary key of the provided type, if any.
 *
 * The primary key is determined by the `@primary` tag on a `readonly` property.
 * If no such property exists, the primary key is assumed to be the one named
 * `id`, if available and `readonly`.
 *
 * If no suitable property is found, this function returns `undefined`.
 *
 */
export function GetPrimaryKey(type: Type): Property | undefined {
    const props = GetTypeProps(type)
    return (
        props.find((p) => p.tags?.primary && p.isReadonly) ??
        props.find((p) => p.name === "id" && p.isReadonly)
    )
}
